import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Plus, Search } from 'lucide-react';

interface OrderCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onOrderCreated: () => void;
}

interface Profile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
}

interface SubOrderItem {
  product_id?: string | null;  // Added product_id
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  delivery_cost: number;
  delivery_option: 'pickup_only' | 'delivery_only' | 'both';
  delivery_address: string | null;
  pickup_address: string | null;
  notes: string | null;
}

const OrderCreateDialog = ({ isOpen, onClose, onOrderCreated }: OrderCreateDialogProps) => {
  const [orderType, setOrderType] = useState<'existing' | 'guest'>('existing');
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [subOrders, setSubOrders] = useState<SubOrderItem[]>([{
    product_name: '',
    quantity: 1,
    unit_price: 0,
    total_price: 0,
    delivery_cost: 0,
    delivery_option: 'pickup_only',
    delivery_address: null,
    pickup_address: null,
    notes: null
  }]);
  const [creating, setCreating] = useState(false);
  // Product Search State
  const [products, setProducts] = useState<{ id: string; name: string; price_per_unit: number; total_inventory: number }[]>([]);
  const [productSearch, setProductSearch] = useState('');

  // Load products for selection
  useEffect(() => {
    const fetchProducts = async () => {
      const { data } = await supabase
        .from('products')
        .select('id, name, price_per_unit, total_inventory')
        .order('name');
      if (data) setProducts(data);
    };
    fetchProducts();
  }, []);

  const searchUser = async () => {
    if (!searchEmail) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, phone')
        .eq('email', searchEmail)
        .single();

      if (error || !data) {
        toast.error("לא נמצא", {
          description: "לא נמצא משתמש עם האימייל הזה",
        });
        return;
      }

      setSelectedUser(data);
      toast.success("נמצא", {
        description: `משתמש: ${data.first_name} ${data.last_name}`
      });
    } catch (error) {
      console.error('Error searching user:', error);
    }
  };

  const updateSubOrder = (index: number, field: string, value: any) => {
    const newSubOrders = [...subOrders];
    newSubOrders[index] = { ...newSubOrders[index], [field]: value };

    // Recalculate total_price when quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price') {
      newSubOrders[index].total_price = newSubOrders[index].quantity * newSubOrders[index].unit_price;
    }

    setSubOrders(newSubOrders);
  };

  const handleProductSelect = (index: number, productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      const newSubOrders = [...subOrders];
      newSubOrders[index] = {
        ...newSubOrders[index],
        product_id: product.id,
        product_name: product.name,
        unit_price: product.price_per_unit,
        total_price: product.price_per_unit * newSubOrders[index].quantity
      };
      setSubOrders(newSubOrders);
    }
  };

  const addSubOrder = () => {
    setSubOrders([...subOrders, {
      product_id: null,
      product_name: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      delivery_cost: 0,
      delivery_option: 'pickup_only',
      delivery_address: null,
      pickup_address: null,
      notes: null
    }]);
  };

  const removeSubOrder = (index: number) => {
    if (subOrders.length <= 1) {
      toast.error("שגיאה", {
        description: "חייבת להיות לפחות פריט אחד בהזמנה",
      });
      return;
    }
    setSubOrders(subOrders.filter((_, i) => i !== index));
  };

  const createOrder = async () => {
    // Validation
    if (orderType === 'existing' && !selectedUser) {
      toast.error("שגיאה", {
        description: "נא לבחור משתמש",
      });
      return;
    }

    if (orderType === 'guest' && (!guestEmail || !guestFirstName || !guestLastName)) {
      toast.error("שגיאה", {
        description: "נא למלא את כל השדות הנדרשים",
      });
      return;
    }

    if (subOrders.some(sub => !sub.product_name || sub.quantity <= 0 || sub.unit_price <= 0)) {
      toast.error("שגיאה", {
        description: "נא למלא את כל פרטי המוצרים",
      });
      return;
    }

    setCreating(true);
    try {
      const total_amount = subOrders.reduce((sum, sub) => sum + sub.total_price, 0);
      const total_delivery_cost = subOrders.reduce((sum, sub) => sum + sub.delivery_cost, 0);

      // Prepare customer details
      let firstName = '';
      let lastName = '';
      let customerEmail = '';
      let customerPhone = '';

      if (orderType === 'existing' && selectedUser) {
        firstName = selectedUser.first_name || '';
        lastName = selectedUser.last_name || '';
        customerEmail = selectedUser.email;
        customerPhone = selectedUser.phone || '';
      } else {
        firstName = guestFirstName;
        lastName = guestLastName;
        customerEmail = guestEmail;
        customerPhone = guestPhone;
      }

      // Create order
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: orderType === 'existing' ? selectedUser!.id : null,
          total_amount,
          total_delivery_cost,
          status: 'processing',
          order_type: 'regular',
          billing_address: {
            first_name: firstName,
            last_name: lastName,
            email: customerEmail,
            phone: customerPhone
          }
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Create sub orders
      for (const subOrder of subOrders) {
        const { error: subError } = await supabase
          .from('sub_orders')
          .insert({
            order_id: order.id,
            product_id: subOrder.product_id || null, // Link product
            product_name: subOrder.product_name,
            quantity: subOrder.quantity,
            unit_price: subOrder.unit_price,
            total_price: subOrder.total_price,
            delivery_cost: subOrder.delivery_cost,
            delivery_option: subOrder.delivery_option,
            delivery_address: subOrder.delivery_address,
            pickup_address: subOrder.pickup_address,
            notes: subOrder.notes,
            status: 'processing'
          });

        if (subError) throw subError;

        // Inventory Deduction Logic
        if (subOrder.product_id) {
          try {
            // Get current inventory
            const { data: currentProduct, error: fetchError } = await supabase
              .from('products')
              .select('total_inventory, liquidation_sale_enabled, liquidation_current_inventory')
              .eq('id', subOrder.product_id)
              .single();

            if (!fetchError && currentProduct) {
              const updates: any = {
                total_inventory: Math.max(0, currentProduct.total_inventory - subOrder.quantity)
              };

              // Update liquidation inventory if enabled
              if (currentProduct.liquidation_sale_enabled && currentProduct.liquidation_current_inventory !== null) {
                updates.liquidation_current_inventory = Math.max(0, currentProduct.liquidation_current_inventory - subOrder.quantity);
              }

              await supabase
                .from('products')
                .update(updates)
                .eq('id', subOrder.product_id);

              console.log(`Updated inventory for product ${subOrder.product_id}`);
            }
          } catch (invError) {
            console.error('Failed to update inventory during admin order creation:', invError);
          }
        }
      }

      // Send confirmation email
      // We pass explicit email/name just in case the edge function doesn't read the new columns yet,
      // though ideally the edge function should be updated to read from order.email/customer_name too.
      // Send confirmation email
      console.log('Invoking send-order-confirmation for new admin order:', order.id);
      const { error: emailInvokeError } = await supabase.functions.invoke('send-order-confirmation', {
        body: { orderId: order.id }
      });

      if (emailInvokeError) {
        console.error('Email invoke failed:', emailInvokeError);
        toast.error("הזמנה נוצרה, אך המייל נכשל", {
          description: `שגיאה: ${emailInvokeError.message || 'Unknown error'}`
        });
      } else {
        console.log('Email invoke successful');
      }

      toast.success("הצלחה", {
        description: "ההזמנה נוצרה בהצלחה"
      });

      onOrderCreated();
      onClose();

      setGuestPhone('');
      setSubOrders([{
        product_name: '',
        quantity: 1,
        unit_price: 0,
        total_price: 0,
        delivery_cost: 0,
        delivery_option: 'pickup_only',
        delivery_address: null,
        pickup_address: null,
        notes: null
      }]);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error("שגיאה", {
        description: "לא ניתן ליצור את ההזמנה",
      });
    } finally {
      setCreating(false);
    }
  };

  const totalAmount = subOrders.reduce((sum, sub) => sum + sub.total_price, 0);
  const totalDelivery = subOrders.reduce((sum, sub) => sum + sub.delivery_cost, 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">יצירת הזמנה חדשה</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Type Selection */}
          <div className="space-y-4">
            <Label>סוג הזמנה</Label>
            <RadioGroup value={orderType} onValueChange={(value: 'existing' | 'guest') => setOrderType(value)}>
              <div className="flex items-center space-x-2 space-x-reverse">
                <RadioGroupItem value="existing" id="existing" />
                <Label htmlFor="existing">קישור למשתמש קיים</Label>
              </div>
              <div className="flex items-center space-x-2 space-x-reverse">
                <RadioGroupItem value="guest" id="guest" />
                <Label htmlFor="guest">הזמנת אורח</Label>
              </div>
            </RadioGroup>
          </div>

          {/* User Selection / Guest Details */}
          {orderType === 'existing' ? (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="חפש לפי אימייל"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={searchUser} variant="outline">
                  <Search className="h-4 w-4 ml-2" />
                  חפש
                </Button>
              </div>
              {selectedUser && (
                <div className="p-4 border rounded-lg bg-muted">
                  <p className="font-medium">{selectedUser.first_name} {selectedUser.last_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                  {selectedUser.phone && <p className="text-sm text-muted-foreground">{selectedUser.phone}</p>}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>שם פרטי *</Label>
                <Input
                  value={guestFirstName}
                  onChange={(e) => setGuestFirstName(e.target.value)}
                />
              </div>
              <div>
                <Label>שם משפחה *</Label>
                <Input
                  value={guestLastName}
                  onChange={(e) => setGuestLastName(e.target.value)}
                />
              </div>
              <div>
                <Label>אימייל *</Label>
                <Input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                />
              </div>
              <div>
                <Label>טלפון</Label>
                <Input
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Sub Orders */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">פריטי ההזמנה</h3>
              <Button onClick={addSubOrder} variant="outline" size="sm">
                <Plus className="h-4 w-4 ml-2" />
                הוסף פריט
              </Button>
            </div>

            {subOrders.map((subOrder, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">פריט {index + 1}</h4>
                  {subOrders.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSubOrder(index)}
                    >
                      מחק
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>שם המוצר *</Label>
                    <div className="space-y-2">
                      {/* Product Select logic */}
                      <Select
                        value={subOrder.product_id || "manual"}
                        onValueChange={(val) => {
                          if (val === "manual") {
                            updateSubOrder(index, 'product_id', null);
                            updateSubOrder(index, 'product_name', '');
                          } else {
                            handleProductSelect(index, val);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="בחר מתוך המלאי או הזן ידנית" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">-- הזנה ידנית --</SelectItem>
                          {products.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} (מלאי: {p.total_inventory}) - ₪{p.price_per_unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Fallback Manual Input if no product selected or explicit manual mode */}
                      <Input
                        placeholder="שם המוצר (הזנה ידנית)"
                        value={subOrder.product_name}
                        onChange={(e) => updateSubOrder(index, 'product_name', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>אופן אספקה</Label>
                    <Select
                      value={subOrder.delivery_option}
                      onValueChange={(value: 'pickup_only' | 'delivery_only' | 'both') => updateSubOrder(index, 'delivery_option', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pickup_only">איסוף עצמי</SelectItem>
                        <SelectItem value="delivery_only">משלוח</SelectItem>
                        <SelectItem value="both">גמיש</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>כמות *</Label>
                    <Input
                      type="number"
                      value={subOrder.quantity}
                      onChange={(e) => updateSubOrder(index, 'quantity', parseInt(e.target.value) || 0)}
                    />
                  </div>

                  <div>
                    <Label>מחיר ליחידה (₪) *</Label>
                    <Input
                      type="number"
                      value={subOrder.unit_price}
                      onChange={(e) => updateSubOrder(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    />
                  </div>

                  <div>
                    <Label>סה"כ מוצר (₪)</Label>
                    <Input
                      type="number"
                      value={subOrder.total_price}
                      disabled
                    />
                  </div>

                  <div>
                    <Label>עלות משלוח (₪)</Label>
                    <Input
                      type="number"
                      value={subOrder.delivery_cost}
                      onChange={(e) => updateSubOrder(index, 'delivery_cost', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>

                {subOrder.delivery_option !== 'pickup_only' && (
                  <div>
                    <Label>כתובת למשלוח</Label>
                    <Input
                      value={subOrder.delivery_address || ''}
                      onChange={(e) => updateSubOrder(index, 'delivery_address', e.target.value)}
                    />
                  </div>
                )}

                {subOrder.delivery_option !== 'delivery_only' && (
                  <div>
                    <Label>כתובת איסוף</Label>
                    <Input
                      value={subOrder.pickup_address || ''}
                      onChange={(e) => updateSubOrder(index, 'pickup_address', e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <Label>הערות</Label>
                  <Textarea
                    value={subOrder.notes || ''}
                    onChange={(e) => updateSubOrder(index, 'notes', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Order Totals */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-lg font-semibold">
              <span>סה"כ מוצרים:</span>
              <span>₪{totalAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-lg">
              <span>סה"כ משלוח:</span>
              <span>₪{totalDelivery.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-xl font-bold">
              <span>סה"כ לתשלום:</span>
              <span>₪{(totalAmount + totalDelivery).toLocaleString()}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>
              ביטול
            </Button>
            <Button onClick={createOrder} disabled={creating}>
              {creating ? 'יוצר...' : 'צור הזמנה'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderCreateDialog;
